import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  Quote as QuoteIcon,
  Mail,
  HeartHandshake,
} from "lucide-react";

import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import DonateDialog from "@/components/site/DonateDialog";
import DonateFAB from "@/components/site/DonateFAB";
import useSmoothScroll from "@/hooks/useSmoothScroll";
import { TEAM, TEAM_VALUES } from "@/lib/team";
import { BRAND } from "@/lib/site";

/* ------------------------------------------------------------------ */
/* Kinetic word-by-word reveal                                         */
/* ------------------------------------------------------------------ */

function KineticLine({ text, delay = 0, className = "", stagger = 0.06 }) {
  const words = text.split(" ");
  return (
    <span
      aria-label={text}
      className={`inline-block ${className}`}
    >
      {words.map((w, i) => (
        <span
          key={i}
          className="inline-block overflow-hidden align-baseline"
          style={{ verticalAlign: "baseline" }}
        >
          <motion.span
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{
              duration: 0.9,
              delay: delay + i * stagger,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="inline-block"
          >
            {w}
            {i < words.length - 1 ? "\u00A0" : ""}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Editorial marquee                                                   */
/* ------------------------------------------------------------------ */

function Marquee({ children }) {
  return (
    <div
      aria-hidden
      className="relative w-full overflow-hidden select-none py-8 lg:py-12 border-y border-brand-border/70 bg-brand-cream"
      data-testid="team-marquee"
    >
      <div className="flex whitespace-nowrap animate-marquee will-change-transform">
        {[0, 1].map((k) => (
          <div key={k} className="flex items-center gap-14 pr-14">
            {children}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Portrait with parallax + spotlight                                  */
/* ------------------------------------------------------------------ */

function TeamPortrait({ src, alt, mirrored }) {
  const wrap = useRef(null);
  const { scrollYProgress } = useScroll({
    target: wrap,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.05, 1, 1.05]);
  const smoothY = useSpring(y, { stiffness: 60, damping: 20 });
  const smoothScale = useSpring(scale, { stiffness: 60, damping: 20 });

  return (
    <div
      ref={wrap}
      className={`relative aspect-[3/4] w-full rounded-[36px] overflow-hidden ring-1 ring-brand-border bg-brand-navy ${
        mirrored ? "lg:origin-left" : "lg:origin-right"
      }`}
    >
      <motion.img
        src={src}
        alt={alt}
        style={{ y: smoothY, scale: smoothScale }}
        className="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
      />
      {/* spotlight vignette */}
      <div className="absolute inset-0 bg-gradient-to-tr from-brand-navy/70 via-transparent to-transparent" />
      <div
        className="absolute inset-0 opacity-[0.10] mix-blend-multiply pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.55 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />
      {/* clipped frame corners */}
      <svg
        className="absolute top-5 left-5 h-8 w-8 text-brand-gold"
        viewBox="0 0 32 32"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M2 12V2h10" />
      </svg>
      <svg
        className="absolute bottom-5 right-5 h-8 w-8 text-brand-gold"
        viewBox="0 0 32 32"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M30 20v10H20" />
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Team member block                                                   */
/* ------------------------------------------------------------------ */

function TeamMember({ member, index }) {
  const mirrored = index % 2 === 1;
  return (
    <article
      data-testid={`team-member-${member.id}`}
      className="relative"
    >
      <div
        className={`grid lg:grid-cols-12 gap-8 lg:gap-16 items-center ${
          mirrored ? "lg:[&>*:first-child]:order-2" : ""
        }`}
      >
        {/* Portrait */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6 relative"
        >
          <TeamPortrait
            src={
              member.portraitLocal
                ? `${process.env.REACT_APP_BACKEND_URL}${member.portrait}`
                : member.portrait
            }
            alt={member.name}
            mirrored={mirrored}
          />
          <div className="absolute -bottom-4 -right-4 hidden md:flex items-center gap-2 bg-brand-cream ring-1 ring-brand-border rounded-full px-4 py-2">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-gold" />
            <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-brand-forest">
              {member.shortRole}
            </span>
          </div>
        </motion.div>

        {/* Text side */}
        <div className={`lg:col-span-6 ${mirrored ? "lg:pr-8" : "lg:pl-8"}`}>
          {/* Big numeric marker */}
          <div className="flex items-baseline gap-4 mb-6">
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif italic text-[92px] lg:text-[120px] leading-none text-brand-forest"
            >
              {member.number}
            </motion.div>
            <div className="flex-1 h-px bg-brand-border" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-brand-muted font-semibold">
              No. {member.number}
            </span>
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.05 }}
            className="font-serif text-4xl lg:text-6xl leading-[1] tracking-tight text-brand-ink"
          >
            {member.name}
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-3 text-xs uppercase tracking-[0.22em] font-semibold text-brand-forest"
          >
            {member.role}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-8 relative pl-8"
          >
            <QuoteIcon className="absolute left-0 top-1 h-6 w-6 text-brand-gold" />
            <p className="font-serif italic text-2xl lg:text-3xl leading-snug text-brand-ink">
              &ldquo;{member.quote}&rdquo;
            </p>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-6 text-brand-muted text-lg leading-relaxed max-w-xl"
          >
            {member.bio}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 flex flex-wrap gap-2"
          >
            {member.tags.map((t) => (
              <span
                key={t}
                className="px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.16em] font-semibold text-brand-forest bg-brand-forest/8 border border-brand-forest/20"
              >
                {t}
              </span>
            ))}
          </motion.div>
        </div>
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function TeamPage() {
  useSmoothScroll();
  const [donateOpen, setDonateOpen] = useState(false);
  const openDonate = () => setDonateOpen(true);
  const closeDonate = () => setDonateOpen(false);

  // Hero parallax
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bigWordY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const bigWordOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const logoRotate = useTransform(scrollYProgress, [0, 1], [0, 30]);

  useEffect(() => {
    if (donateOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => (document.body.style.overflow = "");
  }, [donateOpen]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div data-testid="team-page" className="App bg-brand-cream">
      <Header onDonate={openDonate} />

      <main className="pt-28 lg:pt-32">
        {/* ============================================================ */}
        {/* HERO — signature kinetic reveal                              */}
        {/* ============================================================ */}
        <section
          ref={heroRef}
          data-testid="team-hero"
          className="relative min-h-[90vh] flex flex-col justify-end overflow-hidden"
        >
          {/* Ambient blobs */}
          <div className="absolute inset-0 pointer-events-none">
            <motion.div
              style={{ rotate: logoRotate }}
              className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-brand-gold/15 blur-3xl"
            />
            <div className="absolute bottom-0 -left-40 h-[520px] w-[520px] rounded-full bg-brand-forest/12 blur-3xl" />
          </div>

          <div className="max-w-7xl w-full mx-auto px-6 sm:px-8 lg:px-12 relative py-10 lg:py-16">
            <Link
              to="/"
              data-testid="team-back-home"
              className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-forest underline-link"
            >
              <ArrowLeft className="h-4 w-4" />
              Back home
            </Link>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="mt-14 flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-forest"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-gold" />
              Behind the mission
            </motion.div>

            {/* Signature kinetic H1 */}
            <motion.h1
              style={{ y: bigWordY, opacity: bigWordOpacity }}
              className="mt-5 font-serif tracking-tight text-brand-ink leading-[0.9]"
            >
              <div className="text-[64px] sm:text-[110px] lg:text-[170px] xl:text-[200px]">
                <KineticLine text="Meet" delay={0.1} />
              </div>
              <div className="text-[64px] sm:text-[110px] lg:text-[170px] xl:text-[200px] italic text-brand-forest">
                <KineticLine text="the women" delay={0.35} />
              </div>
              <div className="text-[64px] sm:text-[110px] lg:text-[170px] xl:text-[200px]">
                <KineticLine text="who lead." delay={0.7} />
              </div>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 max-w-xl text-lg lg:text-xl leading-relaxed text-brand-muted"
            >
              A small team, chosen with care — women stewarding the Foundation
              with the same rigor and tenderness the mothers we serve deserve.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1.7 }}
              className="mt-10 flex items-center gap-6 text-xs uppercase tracking-[0.22em] font-semibold text-brand-muted"
            >
              <span>04 Leaders</span>
              <span className="h-px w-10 bg-brand-border" />
              <span>Dar es Salaam · Tanzania</span>
            </motion.div>
          </div>
        </section>

        {/* ============================================================ */}
        {/* MARQUEE                                                       */}
        {/* ============================================================ */}
        <Marquee>
          {[
            "Meet the team",
            "Karibu",
            "Behind the mission",
            "Nguvu Ya Mwanamke",
            "Stewardship · Sisterhood · Longevity",
            "Empowering women & children",
          ].map((t, i) => (
            <span
              key={i}
              className="font-serif italic text-5xl lg:text-7xl text-brand-ink/85 whitespace-nowrap flex items-center gap-14"
            >
              <span>{t}</span>
              <span className="text-brand-gold text-4xl leading-none">✦</span>
            </span>
          ))}
        </Marquee>

        {/* ============================================================ */}
        {/* MANIFESTO — numbered values                                */}
        {/* ============================================================ */}
        <section
          data-testid="team-manifesto"
          className="relative py-24 lg:py-36 bg-brand-cream"
        >
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
              <div className="lg:col-span-4">
                <div className="sticky top-32">
                  <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-brand-forest">
                    Manifesto
                  </div>
                  <h2 className="mt-4 font-serif text-4xl lg:text-5xl leading-[1.05] text-brand-ink">
                    Three quiet <span className="italic text-brand-forest">rules</span> we lead by.
                  </h2>
                </div>
              </div>
              <div className="lg:col-span-8 space-y-14">
                {TEAM_VALUES.map((v, i) => (
                  <motion.div
                    key={v.label}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                    className="grid grid-cols-12 gap-6 items-start pb-14 border-b border-brand-border last:border-b-0"
                  >
                    <div className="col-span-3 lg:col-span-2 font-serif italic text-brand-forest text-4xl lg:text-5xl leading-none">
                      0{i + 1}
                    </div>
                    <div className="col-span-9 lg:col-span-10">
                      <div className="font-serif text-3xl lg:text-4xl text-brand-ink leading-tight">
                        {v.label}.
                      </div>
                      <p className="mt-3 text-brand-muted text-lg leading-relaxed max-w-xl">
                        {v.body}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================ */}
        {/* TEAM MEMBERS                                                  */}
        {/* ============================================================ */}
        <section
          data-testid="team-members"
          className="relative pt-8 lg:pt-16 pb-24 lg:pb-36 bg-brand-cream space-y-32 lg:space-y-48"
        >
          {TEAM.map((m, i) => (
            <div key={m.id} className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
              <TeamMember member={m} index={i} />
            </div>
          ))}
        </section>

        {/* ============================================================ */}
        {/* JOIN CTA                                                      */}
        {/* ============================================================ */}
        <section
          data-testid="team-cta"
          className="relative py-24 lg:py-32 bg-brand-navy text-white overflow-hidden"
        >
          <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-brand-forest/40 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full bg-brand-gold/15 blur-3xl" />

          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
              <div className="lg:col-span-7">
                <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-gold">
                  A seat at the table
                </div>
                <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1] tracking-tight">
                  Bring your <span className="italic text-brand-gold">skill</span>.<br />
                  Change a mother&rsquo;s <span className="italic text-brand-gold">story</span>.
                </h2>
                <p className="mt-8 text-white/75 text-lg lg:text-xl max-w-xl leading-relaxed">
                  Mentors, therapists, teachers, lawyers, accountants — the
                  Foundation is built by women (and allies) who show up. If your
                  gift can help a mother rise, we want to meet you.
                </p>
              </div>
              <div className="lg:col-span-5 flex flex-col gap-3">
                <Link
                  to="/#get-involved"
                  data-testid="team-join-link"
                  className="btn-gold w-full justify-center text-base py-4"
                >
                  Join the team
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
                <a
                  href={`mailto:${BRAND.email}?subject=Trustee%20/%20Team%20interest`}
                  data-testid="team-email-link"
                  className="btn-outline w-full justify-center !border-white/40 !text-white hover:!bg-white hover:!text-brand-navy"
                >
                  <Mail className="h-4 w-4" />
                  Email us directly
                </a>
                <button
                  onClick={openDonate}
                  data-testid="team-cta-donate"
                  className="btn-outline w-full justify-center !border-white/40 !text-white hover:!bg-white hover:!text-brand-navy"
                >
                  <HeartHandshake className="h-4 w-4" />
                  Or fund a mother&rsquo;s next chapter
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer onDonate={openDonate} />
      <DonateFAB onClick={openDonate} />
      <DonateDialog open={donateOpen} onClose={closeDonate} />
    </div>
  );
}
